/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
    chartZAxisColor,
    getChartColorsSubset
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './uniquetriviausers_styles';



/* ========== ~~~~~~~~~~ TRIVIA POLLS ANALYTICS - UNIQUE TRIVIA USERS ~~~~~~~~~~ ========== */
const UniqueTriviaUsersReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const { overview, details, status } = useSelector((state) => state.triviaAndPolls.uniqueTriviaUsers);
    const { status: engagementRateStatus } = useSelector(state => state.triviaAndPolls.triviaEngagementRate);
    const { filters } = useSelector((state) => state.app);
    const changeType = filters.compareTo;

    // component states
    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    // component constants
    const chartTableColors = getChartColorsSubset(1)
    const products = [
        { id: 'uniqueTriviaUsers', name: 'Unique Trivia Users', color: chartTableColors[0] },
        { id: 'uniqueTriviaUsersRateChange', name: 'Engagement Rate', color: chartZAxisColor }
    ];
    const dates = details ? details[selectedInterval] : {};
    const isChartDataReceived = useMemo(() => status === 'fulfilled' && engagementRateStatus === 'fulfilled', [status, engagementRateStatus]);

    // show aggregate total on load of page
    useEffect(() => {
        if (isChartDataReceived && overview) showAggregateTotal();
    }, [overview, isChartDataReceived]);

    // show aggregate total on chart mouse leave
    useEffect(() => {
        if (showTotal) showAggregateTotal();
    }, [showTotal]);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if (isChartDataReceived && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'trivia');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [isChartDataReceived]);

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0
              ? (product.id === 'uniqueTriviaUsersRateChange' ? overview.period['uniqueTriviaUsersRateChange'] : overview.period[`${product.id}RateChange`]) : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        setChartLegends(productArr);
    };

    const getBarChartKeys = () => products.map(product => product.name).reverse();

    const getBarChartColors = () => products.map(product => product.color).reverse();

    const renderBarChartLegends = () => {
        return chartLegends.map((legend, index) =>
        (
            <ChartLegend
                title={legend.name}
                checkboxColor={legend.color}
                checked={legend.checked}
                onCheck={checked => {
                    const legends = [...chartLegends];
                    legends[index] = {
                        ...legends[index],
                        checked: checked
                    }

                    setChartLegends(legends);
                }}
                value={legend.id === "uniqueTriviaUsersRateChange"
                    ? formatNumber(legend.value, 'percent')
                    : formatNumber(legend.value, 'number')
                }
                changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                changeType={changeType}
                metricType={'trivia'}
            />
        )
        )
    }

    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = { date: formattedDate }
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(productData => productData.checked === true);
                if (checkedProducts.map(productData => productData.id).indexOf(product.id) >= 0) {
                    if (product.id === 'uniqueTriviaUsersRateChange')
                        item['z'] = detailsDateData[product.id];
                    else {
                        item[product.name] = detailsDateData[product.id];
                        item[`${product.name}Color`] = product.color;
                    }
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if (productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                if(product.id === 'uniqueTriviaUsersRateChange') 
                  changeRate = productDetails[`uniqueTriviaUsersRateChange`] ? productDetails[`uniqueTriviaUsersRateChange`] : 0;
                else 
                  changeRate = productDetails[`${product.id}RateChange`] ? productDetails[`${product.id}RateChange`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
    }

    const handleMouseLeave = () => {
        setShowTotal(true);
        setSelectedPeriodX('');
    }


    const uniqueTriviaUsersBarChart = {
        indexBy: 'date',
        data: transformBarChartData(),
        keys: getBarChartKeys(),
        colors: getBarChartColors(),
        legends: renderBarChartLegends()
    }

    const cardtoggles = (
        <CardToggles>
            <CardToggle
                label={'Unique Trivia Users'}
                compareType={changeType}
                metricType={'trivia'}
                percentChange={{
                    value: overview?.period ? formatNumber(overview.period.uniqueTriviaUsersRateChange, 'percent') : 0,
                    direction: overview?.period ? getChangeRateDirection(overview.period.uniqueTriviaUsersRateChange) : 'up'
                }}
                selected={true}
                value={overview?.period ? formatNumber(overview.period.uniqueTriviaUsers) : ''}
            />
        </CardToggles>
    );

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={uniqueTriviaUsersBarChart.colors}
            keys={uniqueTriviaUsersBarChart.keys}
            data={uniqueTriviaUsersBarChart.data}
            period={selectedInterval}
            loading={status === 'pending' ? true : false}
            onHover={handleHoverOnBarChart}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            zType={'percentage'}
            zAxisId={'uniqueTriviaUsersRateChange'}
            zAxisColor={chartZAxisColor}
        />
    );

    const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'trivia'}
            products={products}
            data={details ? details[selectedInterval] : null}
            csvFileName={'Unique Trivia Users'}
        />
    ) : []

    const isNoData = () => {
        return (status === 'rejected') ||
            (status === 'fulfilled' && !overview?.period?.uniqueTriviaUsers) ||
            (status === 'fulfilled' && Object.keys(details[selectedInterval]).length === 0);
    }

    return (
        <div name={'uniquetriviausersreport'} class={`${props.classes.uniqueTriviaUsersReport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={'Unique Trivia Users'}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>Trivia & Polls Unique Active Users</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>
                                A Unique User for Trivia & Polls is defined by a user who answered at least one card during the rolled-up time period (daily, weekly, monthly, etc). Engagement Rate is calculated by taking the total number of trivia answers and dividing it by the UNIQUE viewable trivia units on the page
                            </p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={barchart}
                noData={isNoData()}
                enabledPeriods={enabledRollups}
                chartLegend={uniqueTriviaUsersBarChart.legends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default UniqueTriviaUsersReport;